import { APP_INITIALIZER, inject, LOCALE_ID, NgModule } from '@angular/core';
import { ToastrModule } from 'ngx-toastr';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HttpLoaderFactory } from './app.translate-loader';

import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';

import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

import { GridPaginationComponent } from './shared/grid-pagination/grid-pagination.component';
import { DashboardComponent } from './feature/dashboard/dashboard.component';
import { AuthConfigModule } from './auth-config.module';

import { PopupDialogComponent } from './shared/popup-dialog/popup-dialog.component';
import { RenameSurveyDialogComponent } from './shared/rename-survey-dialog/rename-survey-dialog.component';
import { RenameAssetDialogComponent } from './shared/rename-asset-dialog/rename-asset-dialog.component';
import { DeleteAssetDialogComponent } from './shared/delete-asset-dialog/delete-asset-dialog.component';
import { MoveAssetDialogComponent } from './shared/move-asset-dialog/move-asset-dialog.component';

import { NgxFileDropModule } from 'ngx-file-drop';
import { CustomDatePipe } from './pipes/custom-date.pipe';
import { CustomDateFormatPipe } from './pipes/custom-dateformat.pipe';
import { MapsComponent } from './feature/maps/maps.component';
import { ResolutionComponent } from './feature/resolution/resolution.component';
import { SurveyBacklogComponent } from './feature/survey-backlog/survey-backlog.component';
import { NgxsModule } from '@ngxs/store';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { states, storageSettingForStore } from './core/store';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NotificationComponent } from './shared/notification/notification.component';
import { MatMenuModule } from '@angular/material/menu';
import { TopNavigationComponent } from './shared/navigation-bar/top-navigation/top-navigation.component';
import { LeftNavigationComponent } from './shared/navigation-bar/left-navigation/left-navigation.component';
import { SidebarComponent } from './shared/navigation-bar/sidebar/sidebar.component';
import { AddAssetModalComponent } from './feature/add-asset-modal/add-asset-modal.component';
import {
  AuthInterceptor,
  EventTypes,
  PublicEventsService,
} from 'angular-auth-oidc-client';
import { filter } from 'rxjs';
import { UnauthorizedComponent } from './feature/unauthorized/unauthorized.component';
import { BreadcrumbComponent } from './shared/breadcrumb/breadcrumb.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ExportMapModalComponent } from './shared/export-map-modal/export-map-modal.component';
import { RunReportComponent } from './shared/run-report/run-report.component';
import { MatListModule } from '@angular/material/list';
import { AddSurveyRouteModalComponent } from './feature/add-survey-route-modal/add-survey-route-modal.component';
import { L10N_PREFIX, LocalizationService } from '@progress/kendo-angular-l10n';
import { LocalizationLoaderService } from './localization-loader.service';
import { SurveyRouteEditModalComponent } from './shared/survey-route-edit-modal/survey-route-edit-modal.component';
import { ReportTemplateEditModalComponent } from './shared/report-template-edit-modal/report-template-edit-modal.component';
import { SaveNewTemplateModalComponent } from './shared/save-new-template-modal/save-new-template-modal.component';

import { ExcelExportComponentComponent } from './shared/excel-export-component/excel-export-component.component';
import { DatePipe } from '@angular/common';
import { CustomPaginationComponent } from './shared/custom-pagination/custom-pagination.component';
import { MTXMemberOverviewComponent } from './feature/mtx-member-overview/mtx-member-overview.component';
import { NoAccessComponent } from './feature/no-access/no-access.component';
import { LoginModule } from './feature/login/login.module';
import { ProfileMenuComponent } from './feature/profile-menu/profile-menu.component';
import { AuthReturnComponent } from './feature/auth-return/auth-return.component';
import { FileUploadPopupComponent } from './shared/file-upload-popup/file-upload-popup.component';
import { MaintenanceBannerComponent } from './shared/maintenance-banner/maintenance-banner.component';
import { PcDeletionToastComponent } from './shared/components/pc-deletion-toast/pc-deletion-toast.component';
import { ImportProgressPopupComponent } from './shared/components/import-progress-popup/import-progress-popup.component';
import { ResetCompanyDataDialogComponent } from './shared/components/reset-company-data-dialog/reset-company-data-dialog.component';
import { SharedModule } from './shared/shared.module';
import { defineFont } from '@progress/kendo-drawing/pdf';
import { OpenFeatureModule } from '@openfeature/angular-sdk';
import { ConfigCatWebProvider } from '@openfeature/config-cat-web-provider';
import { environment } from '../environments/environment';
import { ConfigCatHealthService } from './core/services/configcat-health.service';
import { FeatureFlagContextService } from './core/services/feature-flag-context.service';
import { createLocalFeatureFlagProvider } from './core/services/local-feature-flag.provider';

/**
 * Reference populated by AppModule constructor so that ConfigCat setupHooks
 * (which fire asynchronously outside Angular's injection context) can call
 * the DI-managed health service.
 */
let configCatHealthRef: ConfigCatHealthService | null = null;

/**
 * Chooses the OpenFeature provider for this build.
 *
 * An EMPTY `configCatSdkKey` selects the inert in-memory provider, which keeps the fully-local stack
 * hermetic: without this, the SPA dials ConfigCat's CDN at boot on every local run and every e2e run,
 * even though everything else in the local stack (OIDC, GraphQL, REST, S3, mail) is containerised.
 *
 * Keyed on the KEY, not on `environment.name`, because environment.ts — the default, staging-pointing
 * environment — also carries `name: 'LOCAL'`. The key is the honest discriminator: no key means there
 * is nothing to call.
 *
 * Deployed environments are unaffected: they ship a real key, so they take the ConfigCat branch with
 * its health hooks exactly as before.
 */
export function createFeatureFlagProvider() {
  if (!environment.configCatSdkKey) {
    console.info(
      '[FeatureFlags] No configCatSdkKey configured — using the offline in-memory provider. ' +
        'Flags resolve to their documented defaults; no ConfigCat network call is made.',
    );
    return createLocalFeatureFlagProvider();
  }

  // maxInitWaitTimeSeconds is left at the SDK default (5 s). FeatureFlagContextService waits for this
  // provider to settle before applying the company context (SYRC-7533), so a negative value (wait without a
  // limit) would hold back isHierarchyPhase1Enabled() indefinitely; cap that wait if this is ever set.
  return ConfigCatWebProvider.create(environment.configCatSdkKey, {
    setupHooks: (hooks) => {
      hooks.on('clientError', () => {
        configCatHealthRef?.markUnhealthy();
      });
      hooks.on('configFetched', (result) => {
        if (result.isSuccess) {
          configCatHealthRef?.markHealthy();
        }
      });
    },
  });
}

@NgModule({
  declarations: [
    AppComponent,
    AuthReturnComponent,
    GridPaginationComponent,
    DashboardComponent,
    PopupDialogComponent,
    RenameSurveyDialogComponent,
    RenameAssetDialogComponent,
    DeleteAssetDialogComponent,
    MoveAssetDialogComponent,
    MapsComponent,
    ResolutionComponent,
    SurveyBacklogComponent,
    TopNavigationComponent,
    LeftNavigationComponent,
    SidebarComponent,
    NotificationComponent,
    AddAssetModalComponent,
    UnauthorizedComponent,
    BreadcrumbComponent,
    ExportMapModalComponent,
    RunReportComponent,
    AddSurveyRouteModalComponent,
    SurveyRouteEditModalComponent,
    ReportTemplateEditModalComponent,
    SaveNewTemplateModalComponent,
    ExcelExportComponentComponent,
    CustomPaginationComponent,
    MTXMemberOverviewComponent,
    NoAccessComponent,
    ProfileMenuComponent,
    FileUploadPopupComponent,
    MaintenanceBannerComponent,
    PcDeletionToastComponent,
    ImportProgressPopupComponent,
    ResetCompanyDataDialogComponent,
  ],
  imports: [
    BrowserModule,
    ToastrModule.forRoot(),
    AppRoutingModule,
    SharedModule,
    LoginModule,
    AuthConfigModule,
    BrowserAnimationsModule,
    MatMenuModule,
    MatFormFieldModule,
    NgxFileDropModule,
    NgxsModule.forRoot(states),
    NgxsReduxDevtoolsPluginModule.forRoot(),
    NgxsStoragePluginModule.forRoot({
      keys: storageSettingForStore,
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),

    OpenFeatureModule.forRoot({ provider: createFeatureFlagProvider() }),

    MatListModule,
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    CustomDatePipe,
    CustomDateFormatPipe,
    DatePipe,
    provideAnimationsAsync('noop'),
    { provide: L10N_PREFIX, useValue: 'kendo.grid' },
    LocalizationService, // Provide the Kendo LocalizationService here
    LocalizationLoaderService,
    { provide: LOCALE_ID, useValue: 'en' },
    {
      provide: APP_INITIALIZER,
      useFactory: registerFontsFactory,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  private readonly eventService = inject(PublicEventsService);
  constructor() {
    // Wire the ConfigCat health service reference for setupHooks callbacks
    configCatHealthRef = inject(ConfigCatHealthService);
    // Keep the feature-flag evaluation context on the current company (SYRC-7533)
    inject(FeatureFlagContextService);
    // Log when the configuration is loaded
    this.eventService
      .registerForEvents()
      .pipe(
        filter((notification) => notification.type === EventTypes.ConfigLoaded),
      )
      .subscribe((notification) => {
        console.log('Config loaded:', notification);
      });

    // Log when silent renew starts (for debugging)
    this.eventService
      .registerForEvents()
      .pipe(
        filter(
          (notification) => notification.type === EventTypes.SilentRenewStarted,
        ),
      )
      .subscribe(() => {
        console.log(
          'Silent renew started at:',
          new Date().toLocaleTimeString(),
        );
      });

    // Log silent renew failures for diagnostics only. Do NOT call authorize()
    // here — recovery is owned by AppComponent, which routes both its startup
    // reconcileAuthState() check and its own SilentRenewFailed handler through
    // a single one-shot latch (reauthenticate()). Calling authorize() from this
    // module subscription as well would bypass that latch and race the other
    // path, producing duplicate redirects with mismatched state/nonce values
    // that cause the STS to render a blank page.
    this.eventService
      .registerForEvents()
      .pipe(
        filter(
          (notification) => notification.type === EventTypes.SilentRenewFailed,
        ),
      )
      .subscribe((notification) => {
        console.error('Silent renew failed:', notification);
      });
  }
}
export function registerFontsFactory() {
  return () => {
    defineFont({
      Roboto: 'assets/fonts/Roboto-Regular.ttf',
    });

    defineFont({
      'Roboto|Bold': 'assets/fonts/Roboto-Bold.ttf',
    });
  };
}
